import React, { useState, useEffect, useCallback, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Link, useLocation } from "react-router-dom";
import classNames from "classnames";
import { has, isEmpty, intersection } from "lodash";
import Avatar from "react-avatar";
import ReactTooltip from "react-tooltip";
import { logout } from "store/actions/authAction";
import { getPermissions } from "store/actions/roleAction";
import {
  getTree,
  setActiveTab,
  clearSelectedFile
} from "store/actions/fileAction";
import { getMe } from "store/actions/userAction";
import { hasProperty } from "helpers/object";
import Profile from "components/Profile";
import Notification from "components/Notification";
import {
  STATUS_SENT,
  STATUS_REJECTED,
  STATUS_PENDING_REVIEW,
  STATUS_DISCUSSIONS,
  STATUS_UPLOADED,
  STATUS_READY_TO_UPLOAD
} from "helpers/fileStatus.json";
import {
  ACCESS_ACCURACY_REPORT,
  UPLOAD_DOCS_TO_ADAPTER,
  MANAGE_METADATA,
  MANAGE_CLIENTS,
  MANAGE_ROLES,
  MANAGE_DEPARTMENTS,
  MANAGE_FILE_HISTORY,
  MANAGE_AI
} from "helpers/role";
import { pendingStatuses } from "helpers/file";
import Translate from "helpers/Translate";

export const Header = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const isManageLayout = location.pathname.startsWith("/manage");
  const selectedClient = useSelector(state => state.client.selectedClient);
  const permissions = useSelector(state => state.role.getPermissions);
  const isLoadingFileCount = useSelector(
    state => state.file.isLoadingFileCount
  );
  const getFileCount = useSelector(state => state.file.getFileCount);
  const user = useSelector(state => state.user.getMe);
  const selectedSubordinate = useSelector(state => state.file.getSubordinate);

  const [currentTab, setCurrentTab] = useState(null);
  const [loggedInUser, setLoggedInUser] = useState("");
  const [pendingCount, setPendingCount] = useState(0);
  const [rejectedCount, setRejectedCount] = useState(0);
  const [discussionCount, setDiscussionCount] = useState(0);
  const [uploadedCount, setUploadedCount] = useState(0);
  const [showProfile, setShowProfile] = useState(false);

  // Show error if client is not set
  const clientWarningShown = useRef(false);
  useEffect(() => {
    if (
      clientWarningShown.current === false &&
      selectedClient === null &&
      has(loggedInUser, "role") &&
      loggedInUser.role?.name !== "superadmin"
    ) {
      clientWarningShown.current = true;
      Notification.warning(
        Translate({
          id: "no_default_client_is_selected_please_select_a_default_client"
        })
      );
    }
  }, [selectedClient, loggedInUser]);

  const fetchTree = useCallback(
    tabName => {
      if (isEmpty(selectedClient) || !currentTab || !selectedSubordinate) {
        return;
      }
      switch (tabName) {
        case STATUS_SENT:
        default:
          dispatch(
            getTree({
              client: selectedClient,
              fileStatus: pendingStatuses,
              subordinateId: selectedSubordinate?.id || ""
            })
          );
          break;
        case STATUS_REJECTED:
          dispatch(
            getTree({
              client: selectedClient,
              fileStatus: `${STATUS_REJECTED}`,
              subordinateId: selectedSubordinate?.id || ""
            })
          );
          break;
        case STATUS_DISCUSSIONS:
          dispatch(
            getTree({
              client: selectedClient,
              fileStatus: `${STATUS_DISCUSSIONS}`,
              subordinateId: selectedSubordinate?.id || ""
            })
          );
          break;
        case STATUS_UPLOADED:
          dispatch(
            getTree({
              client: selectedClient,
              fileStatus: `${STATUS_UPLOADED}`,
              subordinateId: selectedSubordinate?.id || ""
            })
          );
          break;
      }
    },
    [dispatch, selectedClient, selectedSubordinate, currentTab]
  );

  // Load tree when loading for the first time
  useEffect(() => {
    if (selectedClient) {
      if (currentTab === null) {
        fetchTree(STATUS_SENT);
      } else {
        fetchTree(currentTab);
      }
    }
  }, [selectedClient, currentTab, fetchTree]);

  // Load file count
  useEffect(() => {
    if (isLoadingFileCount) {
      setPendingCount("...");
      setRejectedCount("...");
      setDiscussionCount("...");
      setUploadedCount("...");
    }
  }, [isLoadingFileCount]);

  // Get the details of the logged-in person
  useEffect(() => {
    if (!user) {
      dispatch(getMe());
    } else {
      setLoggedInUser(user);
    }
  }, [user, dispatch]);

  // Set the active tab
  useEffect(() => {
    const activeTabStorage = localStorage.getItem("active_tab");
    if (activeTabStorage) {
      setCurrentTab(activeTabStorage);
      dispatch(setActiveTab(activeTabStorage));
    } else {
      setCurrentTab(STATUS_SENT);
      localStorage.setItem("active_tab", STATUS_SENT);
      dispatch(setActiveTab(STATUS_SENT));
    }
  }, [dispatch]);

  // Get Permissions
  useEffect(() => {
    if (!permissions) {
      dispatch(getPermissions());
    }
  }, [permissions, dispatch]);

  // Get and set the file status count
  useEffect(() => {
    if (!getFileCount) {
      setPendingCount("...");
      setRejectedCount("...");
      setDiscussionCount("...");
      setUploadedCount("...");
    } else if (!isEmpty(getFileCount)) {
      setPendingCount(getFileCount[STATUS_PENDING_REVIEW]);
      setRejectedCount(getFileCount[STATUS_REJECTED]);
      setDiscussionCount(getFileCount[STATUS_DISCUSSIONS]);
      setUploadedCount(getFileCount[STATUS_UPLOADED]);
    }
  }, [getFileCount, dispatch]);

  const handleSetActiveTab = tabName => {
    dispatch(clearSelectedFile());
    dispatch(getMe());
    fetchTree(tabName);
    localStorage.setItem("active_tab", tabName);
    setCurrentTab(tabName);
    dispatch(setActiveTab(tabName));
  };

  const canUploadToAdapter = () => {
    return permissions && permissions.includes(UPLOAD_DOCS_TO_ADAPTER);
  };

  return (
    <div
      className={classNames("header", {
        "header-manage": isManageLayout
      })}
    >
      <ReactTooltip />
      <div className="logo">
        <Link to={isManageLayout ? "/manage" : "/dashboard"}>
          <img
            src="/img/logo-text-right.png"
            alt="EisenLens"
            className="logo"
          />
        </Link>
      </div>
      {!isManageLayout && (
        <nav className="nav">
          <ul>
            {/* Pending */}
            <li
              className={classNames({
                hide: user?.role?.name === "superadmin"
              })}
              data-tip={Translate({ id: "files_pending_for_approval" })}
            >
              <Link
                className={classNames({
                  selected: currentTab === STATUS_SENT
                })}
                to="/dashboard"
                onClick={() => handleSetActiveTab(STATUS_SENT)}
              >
                {Translate({ id: "pending" })}
                <span data-tip={pendingCount} className="badge-sm badge-grey">
                  {pendingCount > 99 ? "99+" : pendingCount}
                </span>
              </Link>
            </li>

            {/* Rejected */}
            {!canUploadToAdapter() ? (
              <li
                className={classNames({
                  hide: user?.role?.name === "superadmin"
                })}
                data-tip={Translate({ id: "files_rejected_by_manager" })}
              >
                <Link
                  className={classNames({
                    selected: currentTab === STATUS_REJECTED
                  })}
                  to="/dashboard"
                  onClick={() => handleSetActiveTab(STATUS_REJECTED)}
                >
                  {Translate({ id: "rejected" })}
                  <span data-tip={rejectedCount} className="badge-sm badge-red">
                    {rejectedCount > 99 ? "99+" : rejectedCount}
                  </span>
                </Link>
              </li>
            ) : (
              ""
            )}

            {/* Discussions */}
            <li
              className="hide"
              data-tip={Translate({ id: "discussions_for_file_resolution" })}
            >
              <Link
                className={classNames({
                  selected: currentTab === STATUS_DISCUSSIONS
                })}
                to="/dashboard"
                onClick={() => handleSetActiveTab(STATUS_DISCUSSIONS)}
              >
                {Translate({ id: "discussions" })}
                <span
                  data-tip={discussionCount}
                  className="badge-sm badge-purple"
                >
                  {discussionCount > 99 ? "99+" : discussionCount}
                </span>
              </Link>
            </li>

            {/* Uploaded */}
            <li
              className={classNames({
                hide: user?.role?.name === "superadmin"
              })}
              data-tip={Translate({ id: "files_uploaded_to_the_adapter" })}
            >
              <Link
                className={classNames({
                  selected: currentTab === STATUS_UPLOADED
                })}
                to="/dashboard"
                onClick={() => handleSetActiveTab(STATUS_UPLOADED)}
              >
                {Translate({ id: "uploaded" })}
                <span data-tip={uploadedCount} className="badge-sm badge-blue">
                  {uploadedCount > 99 ? "99+" : uploadedCount}
                </span>
              </Link>
            </li>

            {/* Accuracy */}
            <li
              data-tip={Translate({ id: "accuracy_reports" })}
              className={classNames({
                hide:
                  ![].concat(permissions).includes(ACCESS_ACCURACY_REPORT) ||
                  !has(selectedClient, "id")
              })}
            >
              <Link
                className={classNames({
                  selected: currentTab === "accuracy"
                })}
                to="/accuracy"
                onClick={() => handleSetActiveTab("accuracy")}
              >
                {Translate({ id: "accuracy" })}
              </Link>
            </li>

            {/* Upload Logs */}
            <li
              data-tip={Translate({
                id: "see_upload_logs_or_initiate_a_new_upload_job"
              })}
              className={classNames({
                hide:
                  ![].concat(permissions).includes(UPLOAD_DOCS_TO_ADAPTER) ||
                  !has(selectedClient, "id")
              })}
            >
              <Link
                className={classNames({
                  selected: currentTab === STATUS_READY_TO_UPLOAD
                })}
                to={`/uploads/clients/${hasProperty(
                  () => selectedClient.id,
                  ""
                )}`}
                onClick={() => handleSetActiveTab(STATUS_READY_TO_UPLOAD)}
              >
                {Translate({ id: "upload_logs" })}
              </Link>
            </li>

            {/* Manage */}
            <li
              data-tip={Translate({
                id: "manage_users_metadata_collections_clients_etc"
              })}
            >
              <Link
                className={classNames({
                  selected: currentTab === "manage",
                  hide:
                    intersection(permissions, [
                      MANAGE_METADATA,
                      MANAGE_CLIENTS,
                      MANAGE_ROLES,
                      MANAGE_DEPARTMENTS,
                      MANAGE_FILE_HISTORY,
                      MANAGE_AI
                    ]).length === 0
                })}
                to="/manage"
                onClick={() => handleSetActiveTab("manage")}
              >
                {Translate({ id: "manage" })}
              </Link>
            </li>
          </ul>
        </nav>
      )}
      <div className="user">
        <h2
          className="username pointer"
          title={loggedInUser.fullname}
          data-tip={Translate({
            id: "click_to_edit_profile",
            values: [loggedInUser.fullname]
          })}
        >
          <Avatar
            name={loggedInUser.fullname || "User"}
            size={isManageLayout ? "40" : "30"}
            round={true}
            color="#f34a47"
            fgColor="#ffffff"
            onClick={() => setShowProfile(true)}
          />
        </h2>
        <Profile showProfile={showProfile} setShowProfile={setShowProfile} />
        {isManageLayout && <span className="user-divider" aria-hidden="true" />}
        <Link to={"#"} className="logout" onClick={() => dispatch(logout())}>
          {Translate({ id: "logout" })}
          {isManageLayout && (
            <i className="fas fa-sign-out-alt logout-icon" aria-hidden="true"></i>
          )}
        </Link>
      </div>
    </div>
  );
};

export default Header;
